/**
 * @version 1.3
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/content/front-end/components/utils.html documentation
 */

/**
 * DEPENDENCIES
 */
import 'url-search-params-polyfill';
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';

class Utils {
    static isTouchDevice() {
        return (
            Boolean(typeof window !== 'undefined' &&
                ('ontouchstart' in window ||
                    (window.DocumentTouch &&
                        typeof document !== 'undefined' &&
                        document instanceof window.DocumentTouch))) ||
            Boolean(typeof navigator !== 'undefined' &&
                (navigator.maxTouchPoints || navigator.msMaxTouchPoints))
        );
    }

    /**
     * Метод полностью очищает весь html элемент.
     * @param {Object} element - DOM-элемент, который необходимо очистить.
     */
    static clearHtml(element) {
        element.innerHTML = '';
    }

    /**
     * Метод вставляет содержимое в блок.
     * @param {Object} element - элемент в который нужно вставить.
     * @param {Object/string} content - вставляемый контент.
     */
    static insetContent(element, content) {
        if (typeof content === 'string') {
            element.insertAdjacentHTML('beforeend', content);
        } else if (typeof content === 'object') {
            element.appendChild(content);
        }
    }

    /**
     * Метод полностью удаляет элемент из DOM-дерева.
     * @param {Object} element - элемент, который необходимо удалить.
     */
    static removeElement(element) {
        // node.remove() не работает в IE11
        element.parentNode.removeChild(element);
    }


    /**
     * Метод показывает элемент.
     * @param {Node} element - элемент, который необходимо показать.
     */
    static show(element) {
        element.style.display = 'block';
    }

    /**
     * Метод скрывает элемент.
     * @param {Node} element - элемент, который необходимо скрыть.
     */
    static hide(element) {
        element.style.display = 'none';
    }

    /**
     * Узнает index элемента в родительской элемент
     * Аналог jquery.index()
     * @param {Node} element - искомый элемент
     * @return {number} - порядковый номер (индекс) в родительском элементе
     */
    static getElementIndex(element) {
        return Array.from(element.parentNode.children).indexOf(element);
    }

    /**
     * Узнает находится ли элемент во вьюпорте
     * @param {Node} element - искомый элемент
     * @param {Number} offset - дополнительный отступ
     * @return {boolean} - возвращает true, если элемент виден на экране, и false наоборот
     */
    static isInViewport(element, offset = 0) {
        const rect = element.getBoundingClientRect();
        const top = rect.top + offset;
        const left = rect.left + offset;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const belowViewport = 0;

        const verticalInView = (top <= windowHeight) && ((top + rect.height) >= belowViewport);
        const horizontalInView = (left <= windowWidth) && ((left + rect.width) >= belowViewport);

        return verticalInView && horizontalInView;
    }

    /**
     * Фиксирует страницу для запрета прокрутки
     * @param {Node} element - кроме данного элемента у всех остальных сбросится скролл
     */
    static bodyFixed(element) {
        /* eslint-disable */
        const allowScroll = (el) => {
            while (el && el !== document.body) {
                if (el.classList.contains('project-card__about-content')) {
                    return true;
                }
                el = el.parentElement;
            }
            return false;
        };

        disableBodyScroll(element, {
            reserveScrollBarGap: true,
            allowTouchMove: allowScroll
        });
        /* eslint-disable */
        const test = element.querySelector('.project-card__about-content');
        if (test) {
            bodyScrollLock.enableBodyScroll(test);
        }
        /* eslint-enable */
        const padding = getComputedStyle(document.body).paddingRight;
        const header = document.querySelector('.header');

        if (header) {
            header.style.paddingRight = padding;
        }
    }

    /**
     * Снимаем фиксирование страницы
     */
    static bodyStatic() {
        clearAllBodyScrollLocks();

        const header = document.querySelector('.header');

        if (header) {
            header.style.paddingRight = '0';
        }
    }

    /**
     * Определяет ширину экрана
     * @returns {Number} Значение ширины экрана
     */
    static getWindowWidth() {
        return window.innerWidth || document.documentElement.clientWidth;
    }

    /**
     * Устанавливает гет-параметры
     * @param {string} url - адрес скрипта/API который будем подключать
     * @param {object} callback - функция обратного вызова, если нужна
     */
    static loadScript(url, callback) {
        const script = document.createElement('script');

        // eslint-disable-next-line consistent-return
        script.onload = () => {
            if (callback) {
                return callback();
            }
        };

        script.src = url;
        document.getElementsByTagName('head')[0].appendChild(script);
    }

    static loadHandler(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.src = url;
            img.onload = (event) => {
                const src = event.currentTarget.getAttribute('src');
                const isSvg = src.indexOf('.svg');

                if (isSvg === -1) {
                    resolve({
                        width : event.currentTarget.width,
                        height: event.currentTarget.height
                    });
                } else {
                    $.get(src, (svgxml) => {
                        const viewBox = svgxml.documentElement.attributes.viewBox.value.split(' ');

                        resolve({
                            width : Number(viewBox[2]),
                            height: Number(viewBox[3])
                        });
                    }, 'xml');
                }
            };
            img.onerror = (error) => {
                reject(error);
            };
        });
    }


    /**
     * Debouncing функции означает, что все вызовы будут игнорироваться до тех пор,
     * пока они не прекратятся на определённый период времени. Только после этого функция будет вызвана.
     * @param {function} fun - Функция выполнения
     * @param {number} time - Миллисекунды
     * @return {function} - Замыкание
     */
    static debounce(fun, time) {
        let lastCall = null;

        return (args) => {
            const previousCall = lastCall;

            lastCall = Date.now();

            if (previousCall && ((lastCall - previousCall) <= time)) {
                clearTimeout(this.lastCallTimer);
            }

            this.lastCallTimer = setTimeout(() => {
                fun(args);
            }, time);
        };
    }

    /**
     * Метод проверяет наличие интернета
     * @return {boolean} - При наличии результатом будет true, а при отсутствии false.
     */
    static checkInternetConnection() {
        return navigator.onLine;
    }

    /**
     * Метод отправляет ajax запрос на сервер.
     * @param {Object} data - отправляемые данные.
     * @param {String} url - маршрут по которому нужно произвести запрос.
     * @param {Object} callback -  функции обратного вызова, при успехе вызовет поле success, а при ошибке - error.
     * @param {String} method - метод отправки запроса
     */
    static send(data, url, callback = {}, method = 'GET') {
        const xhr = new XMLHttpRequest();
        const statusSuccess = 200;

        xhr.open(method, url);

        if (!(data instanceof FormData)) {
            xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        }

        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.send(data);

        xhr.onload = function XHR() {
            if (xhr.status === statusSuccess) {
                const req = JSON.parse(this.responseText);

                if (callback.success) {
                    callback.success(req);
                }
            } else if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };

        xhr.onerror = function XHR() {
            if (callback.error) {
                callback.error(xhr.status);
            }

            if (callback.complete) {
                callback.complete();
            }
        };
    }

    /**
     * Генерация псевдослучайных чисел в заданном  числовом интервале
     * @param {Number} min - минимальное псевдослучайное число
     * @param {Number} max - максимальное псевдослучайное число
     * @return {Number} - возвращает псевдослучайное число в интервале [min, max]
     */
    static random(min, max) {
        return Math.floor((Math.random() * (max - min)) + min);
    }

    /**
     * Конвертирует число или строку в строку с рарядами.
     * @param {string/number} value - число или строка.
     * @return {string} - преобразованная строка.
     */
    static convertToDigit(value) {
        return Number(value) ? Number(value).toLocaleString('ru-Ru') : value;
    }

    /**
     * Выбирает окончание слова.
     * @param {number} n - количество
     * @param {array} words - масcив слов. Например показать еще ['квартиру', 'квартиры', 'квартир']
     * @return {string} - слово в правильном склонении.
     */
    static pluralWord(n, words) {
        /* eslint-disable  */
        const $i = n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
        /* eslint-enable*/

        return words[$i];
    }

    /**
     * Троттлинг функции означает, что функция вызывается не более одного раза в указанный период времени (например, раз в 10 секунд).
     * @param {function} fun - Функция выполнения
     * @param {number} time - Миллисекунды
     * @return {function} - замыкание
     */
    static throttle(fun, time) {
        let lastCall = null;

        return (args) => {
            const previousCall = lastCall;

            lastCall = Date.now();

            // eslint-disable-next-line no-undefined
            if (previousCall === undefined || (lastCall - previousCall) > time) {
                fun(args);
            }
        };
    }

    /**
     * Проверяет является ли сущность дом элементом
     * @param {*} elem - элемент для проверки
     * @returns {boolean} true - является
     */
    static isElement(elem) {
        return Boolean(elem.tagName);
    }

    static closest(element, parent) {
        if (!element || !parent) {
            return false;
        }

        return $(element).closest(parent)[0];
    }

    /**
     * Проверяет, мобилка или нет
     * @returns {Boolean} - true - мобилка
     */
    static isMobile() {
        return Utils.getWindowWidth() < 670;
    }

    /**
     * Определяет положение скролла
     * @returns {Number} Значение положения скролла
     */
    static getScrollTop() {
        return window.pageYOffset || document.documentElement.scrollTop;
    }

    /**
     * Клик по элементу или мимо
     *
     * @param {array} selectors - Строка или массив селектор(а)ов отслеживаемых элемент(а)ов
     * @param {function} outside - Callback если клик вне элемент(а)ов
     * @param {function} inside - Callback если клик по элемент(у)ам
     * @param {boolean} updateElements - Нужно ли каждый раз обновлять элементы при клике
     */
    static clickOutside(selectors, outside = function() {}, inside = function() {}, updateElements = false) {
        if (!selectors) {
            return;
        }

        let elements = [];

        if (selectors.nodeType === Node.ELEMENT_NODE) {
            elements = [[selectors]];
        } else if (typeof selectors == 'string') {
            elements.push(Array.from(document.querySelectorAll(selectors)));
        } else {
            Array.from(selectors).forEach((selector) => {
                elements.push(Array.from(document.querySelectorAll(selector)));
            });
        }

        document.addEventListener('click', (event) => {
            let target = event.target;

            if (updateElements) {
                elements = [];

                if (selectors.nodeType === Node.ELEMENT_NODE) {
                    elements = [[selectors]];
                } else if (typeof selectors == 'string') {
                    elements.push(Array.from(document.querySelectorAll(selectors)));
                } else {
                    Array.from(selectors).forEach((selector) => {
                        elements.push(Array.from(document.querySelectorAll(selector)));
                    });
                }
            }

            do {
                try {
                    // eslint-disable-next-line no-loop-func
                    elements.forEach((element) => {
                        if (element.indexOf(target) !== -1) {
                            inside();

                            // eslint-disable-next-line no-throw-literal
                            throw false;
                        }
                    });
                } catch (error) {
                    return;
                }

                target = target.parentNode;
            } while (target);

            outside();
        });
    }

    /**
     * Проверяет, поддерживает ли устройство touch-события
     * @return {boolean} - возвращает true, если Touch-устройство, и false наоборот
     */
    static isTouch() {
        return Boolean(typeof window !== 'undefined' &&
            ('ontouchstart' in window ||
                (window.DocumentTouch &&
                    typeof document !== 'undefined' &&
                    document instanceof window.DocumentTouch))) ||
            Boolean(typeof navigator !== 'undefined' && (navigator.maxTouchPoints || navigator.msMaxTouchPoints));
    }

    /**
     * декодирование строки из base-64 в Unicode
     * @param {String} str - закодированная строка в base64
     * @returns {String} Декодированная строка
     */
    static fromBase64(str) {
        return decodeURIComponent(escape(window.atob(str)));
    }
}

class Counter {
    constructor(stopValue = 3) {
        this.stopValue = stopValue;
        this.countValue = this.stopValue;
    }

    tick() {
        if (!this.countValue) {
            this.countValue = this.stopValue;

            return true;
        }

        this.countValue--;

        return false;
    }
}

class InternetConnection {
    constructor(callback = () => {
        return null;
    }, interval = 200) {
        this.counter = new Counter(3);

        this.callback = callback;
        this.interval = interval;

        this._bindMethodsContext();
    }

    connect() {
        if (!Utils.checkInternetConnection() && !this.counter.tick()) {
            this._reconnect(this.connect);

            return;
        }

        this.callback();
    }

    _bindMethodsContext() {
        this.connect = this.connect.bind(this);
    }

    /**
     * @Public
     * Возвращает контент с ошибками сети
     * @return {Object} - объект со списком контента для обработки ошибок
     */
    getErrorMessage() {
        return Utils.checkInternetConnection() ?
            '<h1>На сервере произошла ошибка. Повторите запрос позднее</h1>' :
            '<h1>Вы не подключены к интернету. Повторите запрос позднее</h1>';
    }

    _reconnect(callback) {
        this.setInterval = setTimeout(() => {
            callback();
        }, this.interval);
    }
}

export {Utils, InternetConnection};
